import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Country } from "../types/countryTypes";
import { getMyCountries } from "../api/countryApi";
import {
  getSelectedCountry,
  getStoredCountries,
  setSelectedCountry as persistSelectedCountry,
  setStoredCountries,
} from "../utils/countryStorage";

interface CountryContextType {
  /** Países a los que el usuario tiene acceso. */
  countries: Country[];
  /** País actualmente seleccionado (null si aún no se elige). */
  selectedCountry: Country | null;
  /** Reemplaza la lista de países del usuario (y la persiste). */
  setCountries: (countries: Country[]) => void;
  /** Selecciona el país activo (y lo persiste). */
  selectCountry: (country: Country | null) => void;
}

const CountryContext = createContext<CountryContextType | undefined>(undefined);

/**
 * Proveedor del contexto de país. Inicializa su estado desde localStorage, de modo que la
 * selección persiste entre recargas. El interceptor de axios lee el país directamente de
 * localStorage, por lo que el contexto se usa sobre todo para que la UI (p.ej. el Navbar) reaccione.
 */
export const CountryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [countries, setCountriesState] = useState<Country[]>(getStoredCountries());
  const [selectedCountry, setSelectedCountryState] = useState<Country | null>(getSelectedCountry());

  const setCountries = useCallback((next: Country[]) => {
    setStoredCountries(next);
    setCountriesState(next);
  }, []);

  const selectCountry = useCallback((country: Country | null) => {
    persistSelectedCountry(country);
    setSelectedCountryState(country);
  }, []);

  // Al cargar la app con sesión activa, refresca los países del usuario desde el servidor.
  // Así el selector refleja el estado real (asignaciones cambiadas, países desactivados) sin
  // depender del snapshot guardado en el último login.
  useEffect(() => {
    if (!localStorage.getItem("user")) return;
    getMyCountries()
      .then((list) => {
        setStoredCountries(list);
        setCountriesState(list);
        // Reconcilia el país seleccionado si ya no está disponible.
        const current = getSelectedCountry();
        const stillValid = current && list.some((c) => c.id === current.id);
        if (!stillValid) {
          const next = list.length === 1 ? list[0] : null;
          persistSelectedCountry(next);
          setSelectedCountryState(next);
        }
      })
      .catch(() => {
        /* sin conexión / sin sesión: se mantiene lo que haya en localStorage */
      });
  }, []);

  return (
    <CountryContext.Provider value={{ countries, selectedCountry, setCountries, selectCountry }}>
      {children}
    </CountryContext.Provider>
  );
};

export const useCountry = (): CountryContextType => {
  const ctx = useContext(CountryContext);
  if (!ctx) {
    throw new Error("useCountry debe usarse dentro de un CountryProvider");
  }
  return ctx;
};
